import React, { useEffect, ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { WizardProvider, useWizard, WizardStepConfig } from '../contexts/WizardContext';

export interface WizardDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  steps: WizardStepConfig[];
  onComplete: (data: Record<string, any>) => Promise<void> | void;
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  persistenceKey?: string;
  initialData?: Record<string, any>;
  children: ReactNode;
  showResumePrompt?: boolean;
}

interface WizardDialogContentProps {
  onClose: () => void;
  title: string;
  steps: WizardStepConfig[];
  onComplete: (data: Record<string, any>) => Promise<void> | void;
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  children: ReactNode;
  showResumePrompt?: boolean;
  persistenceKey?: string;
}

const WizardDialogContent: React.FC<WizardDialogContentProps> = ({
  onClose,
  title,
  steps,
  onComplete,
  width = 'md',
  children,
  showResumePrompt,
  persistenceKey
}) => {
  const wizard = useWizard();
  const [showResume, setShowResume] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  useEffect(() => {
    wizard.setSteps(steps);
  }, [steps]);

  useEffect(() => {
    // Check for saved state on mount
    if (showResumePrompt && persistenceKey) {
      const hasSavedState = localStorage.getItem(persistenceKey);
      if (hasSavedState) {
        setShowResume(true);
      }
    }
  }, [showResumePrompt, persistenceKey]);

  const handleResume = () => {
    if (persistenceKey) {
      wizard.loadState(persistenceKey);
    }
    setShowResume(false);
  };

  const handleStartFresh = () => {
    if (persistenceKey) {
      wizard.clearSavedState(persistenceKey);
    }
    wizard.reset();
    setShowResume(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleFinish = async () => {
    try {
      setIsSubmitting(true);
      wizard.setIsLoading(true);
      wizard.setError(null);
      await onComplete(wizard.data);
      wizard.complete();
      onClose();
    } catch (err: any) {
      wizard.setError(err.message || 'An error occurred while completing the wizard');
    } finally {
      setIsSubmitting(false);
      wizard.setIsLoading(false);
    }
  };

  const isLastStep = wizard.activeStep === steps.length - 1;
  const currentStep = steps[wizard.activeStep];

  if (wizard.isComplete) {
    return (
      <Dialog open maxWidth={width} fullWidth onClose={handleClose}>
        <DialogTitle>
          {title}
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Wizard Complete!
            </Typography>
            <Typography color="text.secondary">
              Your operation has been completed successfully.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      maxWidth={width}
      fullWidth
      onClose={handleClose}
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogTitle>
        {title}
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {wizard.isLoading && <LinearProgress />}

      <DialogContent>
        {showResume && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Box>
                <Button size="small" onClick={handleResume}>
                  Resume
                </Button>
                <Button size="small" onClick={handleStartFresh}>
                  Start Fresh
                </Button>
              </Box>
            }
          >
            You have a saved wizard in progress. Would you like to resume where you left off?
          </Alert>
        )}

        <Stepper activeStep={wizard.activeStep} sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                optional={
                  step.optional ? (
                    <Typography variant="caption">Optional</Typography>
                  ) : step.description ? (
                    <Typography variant="caption">{step.description}</Typography>
                  ) : null
                }
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {wizard.error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => wizard.setError(null)}>
            {wizard.error}
          </Alert>
        )}

        <Box sx={{ minHeight: 300 }}>
          {children}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={wizard.goBack}
          disabled={!wizard.canGoBack || isSubmitting}
        >
          Back
        </Button>
        {!isLastStep ? (
          <Button
            onClick={wizard.goNext}
            disabled={!wizard.canGoNext || isSubmitting}
            variant="contained"
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleFinish}
            disabled={!wizard.isStepValid(wizard.activeStep) || isSubmitting}
            variant="contained"
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Processing...' : 'Finish'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const WizardDialog: React.FC<WizardDialogProps> = ({
  open,
  onClose,
  title,
  steps,
  onComplete,
  width = 'md',
  persistenceKey,
  initialData,
  children,
  showResumePrompt
}) => {
  if (!open) return null;

  return (
    <WizardProvider initialData={initialData} persistenceKey={persistenceKey}>
      <WizardDialogContent
        onClose={onClose}
        title={title}
        steps={steps}
        onComplete={onComplete}
        width={width}
        showResumePrompt={showResumePrompt}
        persistenceKey={persistenceKey}
      >
        {children}
      </WizardDialogContent>
    </WizardProvider>
  );
};
